import React, { useEffect, useRef, useState } from "react"
import * as styles from "./SwipeableScrolling.module.css"
import useSolutionData, { getFrontmatter } from "../utils/useSolutionData"
import Button from "../Button/Button"
import classNames from "classnames"
import useDeviceType from "../../../../hooks/useDeviceType"

type SwipeableScrollingProps = {
  pages: Array<string>
}

type FrontmatterType = {
  image: string
  title: string
  description: string
  link: string
}

const SwipeableScrolling = ({ pages }: SwipeableScrollingProps) => {
  //
  const [dataPages, setDataPages] = useState<Array<FrontmatterType>>([
    { image: "", title: "", description: "", link: "" },
  ])
  const [activePage, setActivePage] = useState(0)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const solutionsData = useSolutionData()

  const deviceType = useDeviceType()

  useEffect(() => {
    const getData = pages.map(page => {
      if (page.split("/")[1] === "solutions")
        return getFrontmatter(solutionsData, page)
      else return { image: "", title: "", description: "", link: "" }
    })
    setDataPages(getData)
  }, [pages])

  useEffect(() => {
    if (
      !containerRef.current ||
      deviceType === "mobile" ||
      deviceType === "tablet"
    ) {
      return
    }

    const handleScroll = (e: WheelEvent) => {
      e.preventDefault()

      setActivePage(preActivePage => {
        if (e.deltaY > 0) {
          return Math.min(preActivePage + 1, dataPages.length - 1)
        } else if (e.deltaY < 0) {
          return Math.max(preActivePage - 1, 0)
        }
        return preActivePage
      })
    }

    const containerElement = containerRef.current
    containerRef.current.addEventListener("wheel", handleScroll, {
      passive: false,
    })
    return () => {
      containerElement.removeEventListener("wheel", handleScroll)
    }
  }, [containerRef.current, activePage, setActivePage])

  return (
    <div className={styles.container} ref={containerRef}>
      {deviceType !== "mobile" && deviceType !== "tablet" ? (
        <>
          {/* Image */}
          <div className={styles.imageContent}>
            {dataPages.map(({ image, title }, index) => {
              if (activePage - 1 === index)
                return (
                  <img
                    src={image}
                    alt={title}
                    className={styles.upImage}
                    onClick={() => setActivePage(index)}
                  />
                )
              else if (activePage === index)
                return (
                  <img src={image} alt={title} className={styles.mainImage} />
                )
              else if (activePage + 1 === index)
                return (
                  <img
                    src={image}
                    alt={title}
                    className={styles.downImage}
                    onClick={() => setActivePage(index)}
                  />
                )
              else
                return (
                  <img src={image} alt={title} className={styles.otherImage} />
                )
            })}
          </div>

          {/* Detail */}
          <div className={styles.detailContent}>
            <h2>
              {dataPages[activePage].title.split(" ").slice(0, 2).join(" ")}
            </h2>
            <p>{dataPages[activePage].description}</p>
            <Button link={dataPages[activePage].link} fill>
              Read More
            </Button>
          </div>

          {/* Navigation */}
          <ul className={styles.navigationContent}>
            {dataPages.map(({ title }, index) => (
              <li
                className={classNames(styles.item, {
                  [styles.active]: activePage === index,
                })}
                onClick={() => setActivePage(index)}
              >
                {title.split(" ").slice(0, 2).join(" ")}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <ul className={styles.navigationContent}>
          {dataPages.map(({ image, title, description, link }, index) => (
            // Card Content
            <li
              className={classNames(styles.item, {
                [styles.active]: activePage === index,
              })}
              onClick={() => setActivePage(index)}
            >
              {title.split(" ").slice(0, 2).join(" ")}

              <div key={title} className={styles.cardContent}>
                <img src={image} alt={title} />
                <div className={styles.detail}>
                  <h2>{title.split(" ").slice(0, 2).join(" ")}</h2>
                  <p>
                    {description.length > 230
                      ? `${description.slice(0, 230)}...`
                      : description}
                  </p>
                </div>
                <Button link={link} underline>
                  Read More
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default SwipeableScrolling
